import { api } from "../../lib";

export interface LinkedStain {
  id: string;
  code: string;
  description: string;
  stainType: string;
  isAvailableInHouse?: boolean;
  isActive?: boolean;
}

export interface Diagnosis {
  id: string;
  code: string;
  diagnosisCategoryId: string;
  cptId: string;
  icdId: string;
  diagnosisDescription: string;
  microscopicDescription: string | null;
  comments: string | null;
  isActive: boolean;
  scope: "global" | "personal";
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  diagnosisCategory?: { id: string; name: string } | null;
  cptCode?: { id: string; code: string; description: string } | null;
  icdCode?: { id: string; code: string; description: string } | null;
  additionalIcdCodes?: Array<{
    id: string;
    icdCodeId: string;
    position: number;
    icdCode?: { id: string; code: string; description: string } | null;
  }>;
  createdBy?: { id: string; fullName: string } | null;
  stainLinks?: Array<{
    id: string;
    stain: LinkedStain;
  }>;
}

export interface DiagnosisSearchParams {
  search?: string;
  categoryIds?: string[];
  cptIds?: string[];
  icdIds?: string[];
  status?: "active" | "inactive";
  page?: number;
  limit?: number;
}

export interface PaginatedDiagnosisResult {
  data: Diagnosis[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateDiagnosisInput {
  code: string;
  diagnosisCategoryId: string;
  cptId: string;
  icdId: string;
  additionalIcdCodeIds?: string[];
  diagnosisDescription: string;
  microscopicDescription?: string | null;
  comments?: string | null;
  isActive?: boolean;
  scope?: "global" | "personal";
  createdById?: string | null;
}

export interface UpdateDiagnosisInput extends Partial<CreateDiagnosisInput> {
  id: string;
}

export const diagnosisService = {
  getAll: async () => {
    return api.get<Diagnosis[]>("/admin/diagnoses");
  },

  search: async (params: DiagnosisSearchParams) => {
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.append("search", params.search);
    if (params.status) searchParams.append("status", params.status);
    if (params.page) searchParams.append("page", params.page.toString());
    if (params.limit) searchParams.append("limit", params.limit.toString());

    // Handle array parameters
    if (params.categoryIds?.length) {
      params.categoryIds.forEach((id) =>
        searchParams.append("categoryIds", id)
      );
    }
    if (params.cptIds?.length) {
      params.cptIds.forEach((id) => searchParams.append("cptIds", id));
    }
    if (params.icdIds?.length) {
      params.icdIds.forEach((id) => searchParams.append("icdIds", id));
    }

    const queryString = searchParams.toString();
    const url = queryString
      ? `/admin/diagnoses?${queryString}`
      : "/admin/diagnoses";

    return api.get<PaginatedDiagnosisResult>(url);
  },

  getById: async (id: string) => {
    return api.get<Diagnosis>(`/admin/diagnoses/${id}`);
  },

  create: async (input: CreateDiagnosisInput) => {
    return api.post<Diagnosis>("/admin/diagnoses", input);
  },

  update: async (input: UpdateDiagnosisInput) => {
    const { id, ...updates } = input;
    return api.put<Diagnosis>(`/admin/diagnoses/${id}`, updates);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    return api.put<Diagnosis>(`/admin/diagnoses/${id}`, {
      isActive,
    });
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/diagnoses/${id}`);
  },

  getLinkedStains: async (diagnosisId: string) => {
    return api.get<LinkedStain[]>(
      `/admin/diagnoses/${diagnosisId}/linked-stains`
    );
  },

  setLinkedStains: async (diagnosisId: string, stainIds: string[]) => {
    return api.put<LinkedStain[]>(
      `/admin/diagnoses/${diagnosisId}/linked-stains`,
      { stainIds }
    );
  },
};
